document.addEventListener("DOMContentLoaded", function () {
  const calmButtonSelector =
    ".wp-block-button.is-style-animated-border .wp-block-button__link";

  function shouldReduceMotion() {
    if (typeof window.matchMedia !== "function") {
      return false;
    }

    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function parseRgbColor(value) {
    const channels = String(value || "").match(/[\d.]+/g);

    if (!channels || channels.length < 3) {
      return null;
    }

    return {
      r: Number(channels[0]),
      g: Number(channels[1]),
      b: Number(channels[2]),
      a: channels.length >= 4 ? Number(channels[3]) : 1,
    };
  }

  function colorWithAlpha(value, alpha) {
    const color = parseRgbColor(value);

    if (!color) {
      return value;
    }

    return `rgba(${
      color.r
    }, ${color.g}, ${color.b}, ${Math.min(color.a, alpha)})`;
  }

  function getRelativeLuminance(value) {
    const color = parseRgbColor(value);

    if (!color) {
      return 0;
    }

    const channels = [color.r, color.g, color.b].map(function (channel) {
      const normalized = channel / 255;

      return normalized <= 0.03928
        ? normalized / 12.92
        : ((normalized + 0.055) / 1.055) ** 2.4;
    });

    return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  }

  function isTransparentColor(value) {
    const color = parseRgbColor(value);

    return !color || color.a <= 0;
  }

  function setCalmProperty(button, property, value) {
    if (button.style.getPropertyValue(property) === value) {
      return;
    }

    button.style.setProperty(property, value);
  }

  function setCalmBoxShadow(button, value) {
    if (button.calmCurrentGlowStack === value) {
      return;
    }

    button.calmCurrentGlowStack = value;
    button.style.boxShadow = value;
  }

  function syncCalmButtonTokens(button) {
    const styles = getComputedStyle(button);
    const backgroundColor = styles.backgroundColor;
    const textColor = styles.color;
    const backgroundLuminance = getRelativeLuminance(backgroundColor);
    const hasVisibleBackground =
      !isTransparentColor(backgroundColor) && backgroundLuminance <= 0.86;
    const hasDarkBackground =
      hasVisibleBackground && backgroundLuminance < 0.28;
    const glowColor = hasVisibleBackground ? backgroundColor : textColor;
    const borderColor = textColor;
    const edgeAlpha = hasDarkBackground ? 0.3 : 0.24;
    const edgeHoverAlpha = hasDarkBackground ? 0.44 : 0.34;
    const glowAlpha = hasDarkBackground ? 0.28 : 0.2;
    const glowHoverAlpha = hasDarkBackground ? 0.38 : 0.28;
    const edgeGlowColor = colorWithAlpha(borderColor, edgeAlpha);
    const edgeGlowHoverColor = colorWithAlpha(borderColor, edgeHoverAlpha);
    const ambientGlowColor = colorWithAlpha(glowColor, glowAlpha);
    const ambientGlowHoverColor = colorWithAlpha(glowColor, glowHoverAlpha);
    const glowStack = [
      `0 0 0 1px ${edgeGlowColor}`,
      `0 0 8px ${edgeGlowColor}`,
      `0 0 18px 2px ${ambientGlowColor}`,
    ].join(", ");
    const glowStackHover = [
      `0 0 0 1px ${edgeGlowHoverColor}`,
      `0 0 10px 1px ${edgeGlowHoverColor}`,
      `0 0 24px 3px ${ambientGlowHoverColor}`,
    ].join(", ");

    setCalmProperty(button, "--calm-accent", borderColor);
    setCalmProperty(
      button,
      "--calm-accent-soft",
      colorWithAlpha(borderColor, 0.5),
    );
    setCalmProperty(button, "--calm-edge-glow-color", edgeGlowColor);
    setCalmProperty(button, "--calm-edge-glow-hover-color", edgeGlowHoverColor);
    setCalmProperty(button, "--calm-ripple", colorWithAlpha(borderColor, 0.2));
    setCalmProperty(button, "--calm-glow-color", ambientGlowColor);
    setCalmProperty(button, "--calm-glow-hover-color", ambientGlowHoverColor);
    setCalmProperty(button, "--calm-glow-stack", glowStack);
    setCalmProperty(button, "--calm-glow-stack-hover", glowStackHover);
    setCalmBoxShadow(
      button,
      button.matches(":hover") || button === document.activeElement
        ? glowStackHover
        : glowStack,
    );
  }

  function observeCalmButtonTokenChanges(button) {
    if (button.calmTokenObserver) {
      return;
    }

    const wrapper = button.closest(".wp-block-button");
    const observer = new MutationObserver(function () {
      syncCalmButtonTokens(button);
    });
    const options = {
      attributes: true,
      attributeFilter: ["class", "style"],
    };

    observer.observe(button, options);

    if (wrapper) {
      observer.observe(wrapper, options);
    }

    button.calmTokenObserver = observer;
  }

  function handleCalmButtonKeyActivation(event) {
    const button = event.currentTarget;
    const isAnchorEnter =
      button instanceof HTMLAnchorElement && event.key === "Enter";

    if (isAnchorEnter || (event.key !== "Enter" && event.key !== " ")) {
      return;
    }

    event.preventDefault();
    button.click();
  }

  function createCalmRipple(button, event) {
    if (shouldReduceMotion()) {
      return;
    }

    const ripple = document.createElement("span");
    ripple.classList.add("ripple");

    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const isPointerClick = event instanceof MouseEvent && event.detail > 0;
    const x = isPointerClick
      ? event.clientX - rect.left - size / 2
      : rect.width / 2 - size / 2;
    const y = isPointerClick
      ? event.clientY - rect.top - size / 2
      : rect.height / 2 - size / 2;

    ripple.style.width = `${size}px`;
    ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;

    const rippleColor =
      getComputedStyle(button).getPropertyValue("--calm-ripple").trim() ||
      "rgba(14, 65, 148, 0.18)";

    ripple.style.background = rippleColor;
    button.appendChild(ripple);

    ripple.animate(
      [
        { transform: "scale(0)", opacity: 0.55 },
        { transform: "scale(1.35)", opacity: 0 },
      ],
      { duration: 420, easing: "cubic-bezier(0.23, 1, 0.32, 1)" },
    ).onfinish = function () {
      ripple.remove();
    };
  }

  function initCalmButtons(root = document) {
    if (!(root instanceof Element) && root !== document) {
      return;
    }

    const buttons = root.matches?.(calmButtonSelector)
      ? [root]
      : Array.from(root.querySelectorAll(calmButtonSelector));

    buttons.forEach(function (button) {
      if (button.dataset.calmInit === "true") {
        return;
      }

      button.dataset.calmInit = "true";
      syncCalmButtonTokens(button);
      observeCalmButtonTokenChanges(button);
      ["mouseenter", "focus"].forEach(function (eventName) {
        button.addEventListener(eventName, function () {
          syncCalmButtonTokens(button);
        });
      });
      ["mouseleave", "blur"].forEach(function (eventName) {
        button.addEventListener(eventName, function () {
          syncCalmButtonTokens(button);
        });
      });
      button.addEventListener("click", function (event) {
        syncCalmButtonTokens(button);
        createCalmRipple(button, event);
      });
      button.addEventListener("keydown", handleCalmButtonKeyActivation);

      if (!button.getAttribute("aria-label")) {
        button.setAttribute("aria-label", button.textContent.trim());
      }
    });
  }

  initCalmButtons();

  const jumpLinkSelector = ".vimeo-jump-link";
  const jumpListSelector = ".custom-spalte-vorsorgeberater-fragestellungen ol";
  const vimeoIframeSelector =
    'iframe.lw-video-section__video[src*="player.vimeo.com"], iframe[src*="player.vimeo.com/video/"]';
  const playerScriptSrc = "https://player.vimeo.com/api/player.js";
  const playerStates = new WeakMap();
  let activeIframe = null;
  let pendingJump = null;
  let playerApiPromise = null;

  function hasVimeoApi() {
    return (
      typeof window.Vimeo !== "undefined" &&
      typeof window.Vimeo.Player === "function"
    );
  }

  function isVisible(element) {
    if (!element) {
      return false;
    }

    const rect = element.getBoundingClientRect();

    return rect.width > 0 && rect.height > 0;
  }

  function getVimeoIframes(scope = document) {
    return Array.from(scope.querySelectorAll(vimeoIframeSelector));
  }

  function syncJumpListCounter(list) {
    if (!(list instanceof HTMLOListElement)) {
      return;
    }

    const start = parseInt(list.getAttribute("start"), 10);
    const counterStart = Number.isNaN(start) ? 0 : start - 1;

    list.style.setProperty("--himml-list-start", String(counterStart));
  }

  function syncJumpListCounters(scope = document) {
    if (scope instanceof HTMLOListElement && scope.matches(jumpListSelector)) {
      syncJumpListCounter(scope);
      return;
    }

    if (!(scope instanceof Element) && scope !== document) {
      return;
    }

    scope.querySelectorAll(jumpListSelector).forEach(syncJumpListCounter);
  }

  function setActiveIframe(iframe) {
    if (iframe?.matches(vimeoIframeSelector)) {
      activeIframe = iframe;
    }
  }

  function getPlayerState(iframe) {
    let state = playerStates.get(iframe);

    if (!state) {
      state = {
        playerPromise: null,
      };
      playerStates.set(iframe, state);
    }

    return state;
  }

  function watchIframe(iframe) {
    if (!iframe || iframe.dataset.vimeoJumpBound === "true") {
      return;
    }

    iframe.dataset.vimeoJumpBound = "true";

    ["pointerdown", "mouseenter", "focus"].forEach(function (eventName) {
      iframe.addEventListener(eventName, function () {
        setActiveIframe(iframe);
      });
    });

    iframe.addEventListener("load", function () {
      setActiveIframe(iframe);
      void ensurePlayer(iframe);
      void flushPendingJump();
    });

    void ensurePlayer(iframe);
  }

  function watchKnownIframes(scope = document) {
    getVimeoIframes(scope).forEach(watchIframe);
  }

  function getScopedIframes(link) {
    const scope =
      link?.closest("article, .entry-content, main, body") || document;

    return getVimeoIframes(scope);
  }

  function getIframeForLink(link) {
    const scopedIframes = getScopedIframes(link);

    if (!scopedIframes.length || !link) {
      return null;
    }

    const linkTop = link.getBoundingClientRect().top;
    const rankedIframes = scopedIframes
      .map(function (iframe) {
        return {
          iframe,
          top: iframe.getBoundingClientRect().top,
        };
      })
      .sort(function (a, b) {
        return b.top - a.top;
      });

    const precedingVisibleMatch = rankedIframes.find(function (entry) {
      return entry.top <= linkTop + 24 && isVisible(entry.iframe);
    });

    if (precedingVisibleMatch) {
      return precedingVisibleMatch.iframe;
    }

    const precedingMatch = rankedIframes.find(function (entry) {
      return entry.top <= linkTop + 24;
    });

    if (precedingMatch) {
      return precedingMatch.iframe;
    }

    return scopedIframes.find(isVisible) || scopedIframes[0] || null;
  }

  function getActiveIframe(link) {
    const relatedIframe = getIframeForLink(link);
    if (relatedIframe) {
      setActiveIframe(relatedIframe);
      return relatedIframe;
    }

    if (activeIframe && document.contains(activeIframe)) {
      return activeIframe;
    }

    const fallbackIframe =
      getVimeoIframes().find(isVisible) || getVimeoIframes()[0];

    if (fallbackIframe) {
      setActiveIframe(fallbackIframe);
    }

    return fallbackIframe || null;
  }

  function loadVimeoPlayerApi() {
    if (hasVimeoApi()) {
      return Promise.resolve(window.Vimeo);
    }

    if (playerApiPromise) {
      return playerApiPromise;
    }

    playerApiPromise = new Promise(function (resolve, reject) {
      let script = document.querySelector(
        'script[data-vimeo-player-api="true"]',
      );

      if (!script) {
        script = document.createElement("script");
        script.src = playerScriptSrc;
        script.async = true;
        script.dataset.vimeoPlayerApi = "true";
        document.head.appendChild(script);
      }

      script.addEventListener("load", function handleLoad() {
        if (hasVimeoApi()) {
          resolve(window.Vimeo);
          return;
        }

        playerApiPromise = null;
        reject(new Error("Vimeo player API loaded without window.Vimeo"));
      });

      script.addEventListener("error", function handleError() {
        playerApiPromise = null;
        reject(new Error("Failed to load Vimeo player API"));
      });
    });

    return playerApiPromise;
  }

  function ensurePlayer(iframe) {
    watchIframe(iframe);

    const state = getPlayerState(iframe);

    if (state.playerPromise) {
      return state.playerPromise;
    }

    state.playerPromise = loadVimeoPlayerApi()
      .then(function () {
        const player = new window.Vimeo.Player(iframe);

        return player.ready().then(function () {
          return player;
        });
      })
      .catch(function (error) {
        state.playerPromise = null;
        throw error;
      });

    return state.playerPromise;
  }

  function getJumpLinkFromEvent(event) {
    if (event.target instanceof Element) {
      const directMatch = event.target.closest(jumpLinkSelector);

      if (directMatch) {
        return directMatch;
      }
    }

    if (typeof event.composedPath === "function") {
      const composedMatch = event.composedPath().find(function (node) {
        return node instanceof Element && node.matches(jumpLinkSelector);
      });

      if (composedMatch) {
        return composedMatch;
      }
    }

    return null;
  }

  function seekPlayer(player, seconds) {
    return Promise.race([
      player.setCurrentTime(seconds),
      new Promise(function (resolve) {
        window.setTimeout(function () {
          resolve(null);
        }, 1500);
      }),
    ]).then(function () {
      return player.play().catch(function () {
        return null;
      });
    });
  }

  function performJump(iframe, seconds) {
    setActiveIframe(iframe);

    return ensurePlayer(iframe)
      .then(function (player) {
        return seekPlayer(player, seconds);
      })
      .catch(function () {
        return false;
      });
  }

  function flushPendingJump() {
    if (!pendingJump) {
      return Promise.resolve(false);
    }

    const nextJump = pendingJump;
    const iframe = getActiveIframe(nextJump.link);

    if (!iframe) {
      return Promise.resolve(false);
    }

    return performJump(iframe, nextJump.seconds).then(function () {
      if (pendingJump === nextJump) {
        pendingJump = null;
      }
      return true;
    });
  }

  document.addEventListener(
    "click",
    function (event) {
      const link = getJumpLinkFromEvent(event);

      if (!link) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const seconds = parseInt(link.getAttribute("data-time"), 10);

      if (Number.isNaN(seconds)) {
        return;
      }

      const iframe = getActiveIframe(link);

      if (iframe) {
        pendingJump = null;
        void performJump(iframe, seconds);
        return;
      }

      pendingJump = {
        link,
        seconds,
      };

      void flushPendingJump();
    },
    true,
  );

  watchKnownIframes();
  syncJumpListCounters();

  if (document.body) {
    const observer = new MutationObserver(function (mutations) {
      let shouldRetryJump = Boolean(pendingJump);

      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (!(node instanceof Element)) {
            return;
          }

          if (node.matches(vimeoIframeSelector)) {
            watchIframe(node);
            shouldRetryJump = true;
          }

          syncJumpListCounters(node);
          initCalmButtons(node);

          if (node.querySelectorAll) {
            const nestedIframes = node.querySelectorAll(vimeoIframeSelector);

            nestedIframes.forEach(function (iframe) {
              watchIframe(iframe);
              shouldRetryJump = true;
            });
          }
        });
      });

      if (shouldRetryJump) {
        void flushPendingJump();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
});
